/**
 * src/lib/offline/prepareOffline.js — warm EVERY tab's offline cache in one shot.
 *
 * CEO request 2026-06-12: an "offline mode" button that programmatically loads
 * the cache of all tabs in depth, so the CEO doesn't have to open each tab
 * online one-by-one before a flight.
 *
 * What this does and does NOT do:
 *   - DATA: fetches every domain's collections and write-throughs them to the
 *     IndexedDB cache (reconcileCollection with online=true). This is the part
 *     that was missing — visiting a tab was the only way to warm its data.
 *   - CODE (subtab JS): NOT handled here. The service worker precache plugin
 *     already caches every emitted JS/CSS chunk at install, so once the app has
 *     loaded online once, every tab + subtab renders offline. Data is the gap.
 *   - IMAGES / heavy media: intentionally excluded — they stay lazy/online per
 *     CEO direction ("anything that is not querying an api endpoint" + "load the
 *     heavy things as used, like images").
 *
 * Sequential per domain (so the UI can show live per-tab progress); collections
 * within a domain fetch in parallel. Best-effort: one domain failing never
 * aborts the rest.
 */

import { reconcileCollection } from './entityCache';
import { getOnline } from './networkStatus';
import { loadCfoData } from './cfoCache';
import { fetchTasks } from '../api/tasks';
import { fetchProjects, fetchBundles, fetchStageFlows, fetchStageFlowSteps } from '../api/projects';
import { fetchCRM, fetchVessels, fetchAllVesselContacts, fetchClients, fetchPorts } from '../api/crm';
import { fetchPeople } from '../api/people';
import { fetchSuppliers, fetchPurchaseOrders } from '../api/procurement';
import { supabase } from '../supabase';
import { logAction, logResult } from '../observability';

const PREPARED_AT_KEY = 'acn_offline_prepared_at';

/** Fetch one collection and write it through to cache; return the row count. */
async function warmCollection(key, fetcher) {
  const fetched = await fetcher().catch(() => { /* spine-emit-ok: per-collection failure surfaces in the domain result + aggregate logResult */ return []; });
  const rows = await reconcileCollection(key, Array.isArray(fetched) ? fetched : [], true);
  return Array.isArray(rows) ? rows.length : 0;
}

async function sum(promises) {
  const counts = await Promise.all(promises);
  return counts.reduce((a, b) => a + b, 0);
}

// Mirror EXACTLY the collection keys each domain hook reads offline, so warming
// here populates the same cache the hooks hydrate from.
const DOMAINS = [
  { key: 'focus', label: 'Focus / Planner', run: () => warmCollection('tasks', fetchTasks) },
  { key: 'projects', label: 'Projects', run: () => sum([
    warmCollection('projects', fetchProjects),
    warmCollection('bundles', () => fetchBundles()),
    warmCollection('stageFlows', fetchStageFlows),
    warmCollection('stageFlowSteps', fetchStageFlowSteps),
  ]) },
  { key: 'pipeline', label: 'Pipeline', run: () => sum([
    warmCollection('crm', fetchCRM),
    warmCollection('vessels', fetchVessels),
    warmCollection('vesselContacts', fetchAllVesselContacts),
    warmCollection('clients', fetchClients),
  ]) },
  { key: 'procurement', label: 'Procurement', run: () => sum([
    warmCollection('suppliers', fetchSuppliers),
    warmCollection('purchaseOrders', fetchPurchaseOrders),
  ]) },
  { key: 'directory', label: 'Directory', run: () => sum([
    warmCollection('people', fetchPeople),
    warmCollection('ports', fetchPorts),
    warmCollection('departments', () => supabase.from('departments')
      .select('id, name, name_es, display_order').order('display_order')
      .then((r) => r.data || [])),
  ]) },
  { key: 'analytics', label: 'Analytics', run: async () => {
    const data = await loadCfoData(true);
    return Object.values(data).reduce((n, v) => n + (Array.isArray(v) ? v.length : 0), 0);
  } },
];

/** Static descriptor of the tabs the warm-all covers (for the UI list). */
export const OFFLINE_DOMAINS = DOMAINS.map((d) => ({ key: d.key, label: d.label }));

/**
 * Warm every domain's cache. Reports progress via onProgress(evt) where evt is
 * { key, label, status:'running'|'done'|'error', count?, error? }.
 * @param {(evt:object)=>void} [onProgress]
 * @returns {Promise<{ok:boolean, offline?:boolean, results:Array<{key,label,count?,ok,error?}>}>}
 */
export async function prepareOfflineCache(onProgress = () => {}) {
  if (!getOnline()) return { ok: false, offline: true, results: [] };

  const cid = logAction('offline:prepare', { domains: DOMAINS.length });
  const results = [];
  for (const d of DOMAINS) {
    try { onProgress({ key: d.key, label: d.label, status: 'running' }); } catch { /* spine-emit-ok: UI progress callback is best-effort, not an app failure */ }
    try {
      const count = await d.run();
      const r = { key: d.key, label: d.label, count, ok: true };
      results.push(r);
      try { onProgress({ ...r, status: 'done' }); } catch { /* spine-emit-ok: UI progress callback is best-effort */ }
    } catch (e) {
      // Per-domain failure is RECORDED in results + surfaced via onProgress, and
      // rolled into the aggregate logResult('partial') below.
      const r = { key: d.key, label: d.label, ok: false, error: e?.message || String(e) };
      results.push(r);
      try { onProgress({ ...r, status: 'error' }); } catch { /* spine-emit-ok: UI progress callback is best-effort */ }
    }
  }

  const ok = results.every((r) => r.ok);
  const total = results.reduce((n, r) => n + (r.count || 0), 0);
  logResult('offline:prepare', ok ? 'success' : 'partial', { correlationId: cid, total });
  try { localStorage.setItem(PREPARED_AT_KEY, String(Date.now())); } catch { /* spine-emit-ok: localStorage write in private mode — stamp is cosmetic */ }
  return { ok, results };
}

/** Epoch ms of the last successful warm, or null. Drives the "Updated …" label. */
export function getLastPreparedAt() {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(PREPARED_AT_KEY) : null;
    return v ? Number(v) : null;
  } catch {
    // spine-emit-ok: localStorage read in private mode — cosmetic timestamp only
    return null;
  }
}
